import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { isFileTooLarge, MAX_UPLOAD_FILE_SIZE_MB } from '../../lib/upload';

export interface SelectedSupportingDocument {
  file: File;
  description?: string;
}

interface SupportingDocumentsPickerProps {
  documents: SelectedSupportingDocument[];
  onAddFiles: (files: File[]) => void;
  onRemoveDocument: (index: number) => void;
  onDescriptionChange: (index: number, description: string) => void;
  disabled?: boolean;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

export const SupportingDocumentsPicker: React.FC<SupportingDocumentsPickerProps> = ({
  documents,
  onAddFiles,
  onRemoveDocument,
  onDescriptionChange,
  disabled = false,
}) => {
  const { t } = useTranslation('invoices');
  const inputRef = useRef<HTMLInputElement>(null);
  const [sizeError, setSizeError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selected = Array.from(e.target.files);
      const oversized = selected.filter(isFileTooLarge);
      const accepted = selected.filter((file) => !isFileTooLarge(file));
      setSizeError(
        oversized.length > 0
          ? t('generator.supportingDocuments.filesTooLarge', {
              count: oversized.length,
              max: MAX_UPLOAD_FILE_SIZE_MB,
              files: oversized.map((file) => file.name).join(', '),
            })
          : null
      );
      if (accepted.length > 0) {
        onAddFiles(accepted);
      }
    }
    // Reset so selecting the same file again still fires a change event
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <Card className="p-4" data-testid="supporting-documents-picker">
      <h3 className="text-lg font-semibold mb-4">
        {t('generator.supportingDocuments.title')}{' '}
        <span className="text-xs font-normal text-muted-foreground">
          {t('generator.supportingDocuments.optional')}
        </span>
      </h3>

      <div className="space-y-2">
        <Label htmlFor="supporting-documents-input">
          {t('generator.supportingDocuments.selectFiles')}
        </Label>
        <Input
          id="supporting-documents-input"
          data-testid="supporting-documents-input"
          ref={inputRef}
          type="file"
          multiple
          disabled={disabled}
          onChange={handleFileChange}
        />
        <p className="text-xs text-muted-foreground">
          {t('generator.supportingDocuments.maxFileHint', { max: MAX_UPLOAD_FILE_SIZE_MB })}
        </p>
      </div>

      {sizeError && (
        <p
          className="mt-2 text-sm text-destructive"
          data-testid="supporting-documents-size-error"
        >
          {sizeError}
        </p>
      )}

      {documents.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">
          {t('generator.supportingDocuments.empty')}
        </p>
      ) : (
        <div className="mt-4 space-y-2">
          {documents.map((doc, index) => (
            <div
              key={`${doc.file.name}-${index}`}
              className="flex items-start justify-between gap-3 p-3 border rounded-lg"
              data-testid="supporting-document-item"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary flex-shrink-0" />
                  <span className="text-sm font-medium truncate">{doc.file.name}</span>
                  <span className="text-xs text-muted-foreground flex-shrink-0">
                    {formatFileSize(doc.file.size)}
                  </span>
                </div>
                <Input
                  className="mt-2"
                  value={doc.description || ''}
                  disabled={disabled}
                  placeholder={t('generator.supportingDocuments.descriptionPlaceholder')}
                  aria-label={t('generator.supportingDocuments.descriptionLabel')}
                  onChange={(e) => onDescriptionChange(index, e.target.value)}
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={disabled}
                onClick={() => onRemoveDocument(index)}
                title={t('generator.supportingDocuments.remove')}
                aria-label={t('generator.supportingDocuments.remove')}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
